import { IDesignControl } from './design-control';
import { TemplateControlAction, ControlActionType, TemplateControlType } from 'src/app/modules/management/store/communication-center';
import { TemplateControlService } from './template-control.service';
import { takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { GlobalEventListenerService } from 'src/app/modules/core/services/global-event-listener.service';

export class SettingsWrapperControl {
  currentControl: IDesignControl;
  content: HTMLElement;
  isEditMode: boolean;
  isVisible: boolean;
  isDragging: boolean;
  savedDragEnter: any;
  savedDragOver: any;
  destroy$: Subject<void> = new Subject<void>();

  constructor(
    public controlElement: HTMLElement,
    public document: Document,
    public eventListenerService: GlobalEventListenerService,
    public templateControlService: TemplateControlService
  ) {
    this.wireUpEvents();

    this.content = controlElement.getElementsByClassName('content')[0] as HTMLElement;
  }

  wireUpEvents() {
    const editButton = this.controlElement.getElementsByClassName('edit-button')[0] as HTMLElement;
    if (editButton) {
      editButton.onclick = this.edit.bind(this);
    }

    const copyButton = this.controlElement.getElementsByClassName('copy-button')[0] as HTMLElement;
    if (copyButton) {
      copyButton.onclick = this.copy.bind(this);
    }

    const deleteButton = this.controlElement.getElementsByClassName('delete-button')[0] as HTMLElement;
    if (deleteButton) {
      deleteButton.onclick = this.delete.bind(this);
    }

    const saveButton = this.controlElement.getElementsByClassName('save-button')[0] as HTMLElement;
    saveButton.onclick = this.save.bind(this);

    const cancelButton = this.controlElement.getElementsByClassName('cancel-button')[0] as HTMLElement;
    cancelButton.onclick = this.cancel.bind(this);

    if (this.templateControlService.enableDrag) {
      const dragHandle = this.controlElement.getElementsByClassName('edit-window')[0] as HTMLElement;
      dragHandle.ondragstart = this.dragStart.bind(this);
      dragHandle.ondragend = this.dragEnd.bind(this);
    }

    this.eventListenerService.clickEvents$
      .pipe(
        filter(
          target =>
            this.isVisible &&
            !this.controlElement.contains(target) &&
            !(this.currentControl.controlType === TemplateControlType.Text && this.isEditMode)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(target => {
        const toolbar = document.getElementById('text-tool-bar');
        if (!!toolbar && toolbar.contains(target)) {
          return;
        }

        if (this.isEditMode) {
          this.save();
        }
        this.hide();
      });
  }

  show(control: IDesignControl) {
    if (control === this.currentControl || this.isEditMode || this.isDragging) {
      return;
    }

    this.clearContent();

    this.currentControl = control;

    this.resize();

    this.showEditButtonBar();

    this.isVisible = true;

    const editButton = this.controlElement.getElementsByClassName('edit-button')[0] as HTMLElement;
    if (editButton) {
      editButton.style.display = control.canEdit ? 'block' : 'none';
    }

    const copyButton = this.controlElement.getElementsByClassName('copy-button')[0] as HTMLElement;
    if (copyButton) {
      copyButton.style.display = control.canCopy ? 'block' : 'none';
    }

    const deleteButton = this.controlElement.getElementsByClassName('delete-button')[0] as HTMLElement;
    if (deleteButton) {
      deleteButton.style.display = control.canDelete ? 'block' : 'none';
    }
  }

  resize() {
    if (this.templateControlService.isPreviewLayout) {
      this.resizePreview();
    } else {
      this.resizeTemplateBuilder();
    }
  }

  resizePreview(): void {
    const targetRect = this.currentControl.controlElement.getBoundingClientRect();
    const documentScrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const documentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    const templateContainer = this.document.getElementsByClassName('design-surface-container')[0];
    const templateScrollLeft = templateContainer.scrollLeft;
    const templateScrollTop = templateContainer.scrollTop;

    const editWindow = this.controlElement.getElementsByClassName('edit-window')[0] as HTMLElement;
    let editWindowHeight = targetRect.height + 12;

    // check for overflow and prevent
    const maxHeight = window.innerHeight;
    if (targetRect.bottom > maxHeight) {
      editWindowHeight = maxHeight - targetRect.top - 160;
    }
    editWindow.style.height = `${editWindowHeight}px`;

    const wrapperTop = targetRect.top - 111;
    this.controlElement.style.top = `${wrapperTop}px`;
    this.controlElement.style.left = `90px`;

    this.controlElement.style.width = `${targetRect.width + 32}px`;
    this.controlElement.style.visibility = 'visible';
  }

  resizeTemplateBuilder(): void {
    const targetRect = this.currentControl.controlElement.getBoundingClientRect();
    const documentScrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const documentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    const templateContainer = this.document.getElementsByClassName('design-surface-container')[0];
    const templateScrollLeft = templateContainer.scrollLeft;
    const templateScrollTop = templateContainer.scrollTop;

    const editWindow = this.controlElement.getElementsByClassName('edit-window')[0] as HTMLElement;
    let editWindowHeight = targetRect.height + 12;

    // check for overflow and prevent
    const maxHeight = window.innerHeight - 36;
    if (targetRect.top + editWindowHeight > maxHeight) {
      editWindowHeight = maxHeight - targetRect.top;
    }
    editWindow.style.height = `${editWindowHeight}px`;

    const wrapperTop = targetRect.top + documentScrollTop - 6;
    this.controlElement.style.top = `${wrapperTop}px`;
    this.controlElement.style.left = `${targetRect.left - documentScrollLeft - templateScrollLeft - 28}px`;

    this.controlElement.style.width = `${targetRect.width + 32}px`;
    this.controlElement.style.visibility = 'visible';
  }

  hide() {
    if (this.isEditMode) {
      this.cancel();
    }

    this.controlElement.style.visibility = 'hidden';

    this.currentControl = null;

    this.isVisible = false;
  }

  edit() {
    this.isEditMode = true;

    this.document.getElementById('edit-window').draggable = false;

    this.showSaveButtonBar();

    this.currentControl.edit();

    const editWindow = this.controlElement.getElementsByClassName('edit-window')[0] as HTMLElement;
    editWindow.style.height = 'auto';
  }

  copy() {
    const copiedControl = this.templateControlService.copyControl(this.currentControl);

    this.show(copiedControl);
  }

  delete() {
    this.templateControlService.removeControl(this.currentControl);

    this.hide();
  }

  destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel() {
    this.isEditMode = false;

    this.document.getElementById('edit-window').draggable = this.templateControlService.enableDrag;

    this.showEditButtonBar();

    this.currentControl.cancel();

    const targetRect = this.currentControl.controlElement.getBoundingClientRect();
    const editWindow = this.controlElement.getElementsByClassName('edit-window')[0] as HTMLElement;
    editWindow.style.height = `${targetRect.height + 10}px`;

    this.hide();
  }

  save() {
    this.isEditMode = false;

    this.document.getElementById('edit-window').draggable = this.templateControlService.enableDrag;

    this.showEditButtonBar();

    this.currentControl.save();

    const targetRect = this.currentControl.controlElement.getBoundingClientRect();
    const editWindow = this.controlElement.getElementsByClassName('edit-window')[0] as HTMLElement;
    editWindow.style.height = `${targetRect.height + 10}px`;

    this.hide();
  }

  showEditButtonBar() {
    const buttonBarEdit = this.controlElement.getElementsByClassName('button-bar-edit')[0] as HTMLElement;
    buttonBarEdit.style.display = 'flex';
    const buttonBarSave = this.controlElement.getElementsByClassName('button-bar-save')[0] as HTMLElement;
    buttonBarSave.style.display = 'none';
  }

  showSaveButtonBar() {
    const buttonBarEdit = this.controlElement.getElementsByClassName('button-bar-edit')[0] as HTMLElement;
    buttonBarEdit.style.display = 'none';
    const buttonBarSave = this.controlElement.getElementsByClassName('button-bar-save')[0] as HTMLElement;
    buttonBarSave.style.display = 'flex';
  }

  dragStart(event) {
    if (!this.templateControlService.enableDrag) {
      return;
    }

    this.isDragging = true;

    if (!this.currentControl) {
      return;
    }

    const dragImage = this.getDragImageElement();

    event.dataTransfer.setDragImage(dragImage, 0, 0);

    const properties: TemplateControlAction = {
      actionType: ControlActionType.Move,
      controlType: this.currentControl.controlType,
      sourceElementId: this.currentControl.id
    };

    const propertyData = JSON.stringify(properties);

    event.dataTransfer.setData('text/plain', propertyData);

    const elm = this.document.getElementById('design-surface');
    this.savedDragEnter = elm.ondragenter;
    this.savedDragOver = elm.ondragover;
    elm.ondragenter = null;
    elm.ondragover = null;

    return true;
  }

  dragEnd(event) {
    if (!this.templateControlService.enableDrag) {
      return;
    }

    event.preventDefault();
    const elm = this.document.getElementById('design-surface');
    elm.ondragenter = this.savedDragEnter;
    elm.ondragover = this.savedDragOver;
    this.isDragging = false;
  }

  setContent(element: HTMLElement) {
    this.content.appendChild(element);
  }

  clearContent() {
    if (!!this.content?.firstElementChild) {
      this.content.removeChild(this.content.firstElementChild);
    }
  }

  getDragImageElement(): HTMLElement {
    let dragImageId = '';

    switch (this.currentControl.controlType) {
      case TemplateControlType.Attachments:
        dragImageId = 'palette-attachments';
        break;
      case TemplateControlType.Button:
        dragImageId = 'palette-button';
        break;
      case TemplateControlType.Columns:
        dragImageId = 'palette-columns';
        break;
      case TemplateControlType.Divider:
        dragImageId = 'palette-divider';
        break;
      case TemplateControlType.FormField:
        dragImageId = 'palette-form-field';
        break;
      case TemplateControlType.Image:
        dragImageId = 'palette-image';
        break;
      case TemplateControlType.Map:
        dragImageId = 'palette-map';
        break;
      case TemplateControlType.SectionHeader:
        dragImageId = 'palette-section-header';
        break;
      case TemplateControlType.SelectedAssetsList:
        dragImageId = 'palette-selected-asset-list';
        break;
      case TemplateControlType.Text:
        dragImageId = 'palette-text';
    }

    const dragImage = document.getElementById(dragImageId);

    return dragImage;
  }
}
